import { useContext, useRef } from 'react'
import styled from 'styled-components'

import { NextDialogProvider } from '../../../app/_components/dialogs/NextDialog.client'
import { VariantSelectionFormContext } from '../../../app/_components/forms/VariantSelectionForm'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import {
  useEmbroidery,
  useEmbroideryDialog,
} from '../../../containers/EmbroideryContainer/EmbroideryProvider'
import { useTransientLogo } from '../../../containers/EmbroideryContainer/useEmbroideryLogos'
import {
  getEmbroideryTypeForEmbroideryForm,
  getFormsForEmbroideryType,
} from '../../../containers/EmbroideryContainer/utils/embroideryFormUtils'

import { EmbroideryModalHeader } from './EmbroideryModalHeader'
import { EmbroideryModalPreviewCarousel } from './EmbroideryModalPreview/EmbroideryModalPreviewCarousel'
import { EmbroideryOptionSelector } from './EmbroideryOptionSelector/EmbroideryOptionSelector'
import * as Styles from './styles'

export const EmbroideryModal = ({ isB2BColorSelected }: { isB2BColorSelected?: boolean }) => {
  const {
    embroidery: { modal: strings, embroideryFormKindTitles, close: closeText },
  } = useTranslation()
  const {
    selectedFormKind,
    setSelectedFormKind,
    setPreviewItemsByKind,
    hasSubmittedEmbroiderySelections,
    submittedEmbroiderySelections,
    isB2bEmbroidery,
    productTitle,
  } = useEmbroidery()
  const transientLogo = useTransientLogo()

  const { isOpen, close, confirmAndClose } = useEmbroideryDialog()

  const variantSelection = useContext(VariantSelectionFormContext)

  const closeBtnRef = useRef<HTMLButtonElement>(null)

  const headerTitle = selectedFormKind ? embroideryFormKindTitles[selectedFormKind] : strings.title

  const handleBackButtonClick = () => {
    // Going back from LOGO_DTC -> LOGO_SETUP stays within the logo flow, so keep the previews
    if (
      selectedFormKind === 'LOGO_DTC' &&
      !submittedEmbroiderySelections[selectedFormKind] &&
      !transientLogo
    ) {
      setSelectedFormKind('LOGO_SETUP')
      return
    }

    // Leaving to the main menu clear the previews for every unsubmitted form kind in the
    // current flow
    if (selectedFormKind) {
      const flowFormKinds = getFormsForEmbroideryType({
        embroideryType: getEmbroideryTypeForEmbroideryForm(selectedFormKind),
        isB2b: isB2bEmbroidery,
      })
      setPreviewItemsByKind(prev => ({
        ...prev,
        ...Object.fromEntries(
          flowFormKinds
            .filter(formKind => !submittedEmbroiderySelections[formKind])
            .map(formKind => [formKind, []])
        ),
      }))
    }
    setSelectedFormKind(null)
  }

  const onClose = isB2BColorSelected || !hasSubmittedEmbroiderySelections ? close : confirmAndClose

  return (
    <NextDialogProvider open={isOpen}>
      <Styles.Dialog
        analyticsTitle='Embroidery'
        variant='default'
        backdrop={Styles.Backdrop}
        backdropTiming={Styles.BACKDROP_TIMING}
        dialogTiming={Styles.DIALOG_TIMING}
        onClose={onClose}
        aria-label={strings.previewTitle}
        content={<Styles.DialogBody />}
        render={props => (
          <Styles.DialogContainer>
            <div {...props} data-testid='embroidery-modal' />
          </Styles.DialogContainer>
        )}
      >
        <Styles.DialogBody>
          <Styles.Container>
            <Styles.Section>
              <EmbroideryModalHeader.Wrapper>
                <EmbroideryModalHeader.Section>
                  {selectedFormKind !== null ? (
                    <EmbroideryModalHeader.BackButton handleClick={handleBackButtonClick} />
                  ) : null}
                  <EmbroideryModalHeader.CloseButtonDesktop
                    onClick={onClose}
                    ref={closeBtnRef}
                    text={closeText}
                    autoFocus
                  />
                </EmbroideryModalHeader.Section>
                <EmbroideryModalHeader.Title>{headerTitle}</EmbroideryModalHeader.Title>
                <EmbroideryModalHeader.Section>
                  <EmbroideryModalHeader.CloseButtonMobile onClick={onClose} ref={closeBtnRef} />
                </EmbroideryModalHeader.Section>
              </EmbroideryModalHeader.Wrapper>
              <EmbroideryPreviewContainer>
                <Styles.ImageHeader>
                  <Styles.Title>
                    {variantSelection?.variantGroup.title || productTitle || ''}
                  </Styles.Title>
                </Styles.ImageHeader>
                <Styles.ImagePreviewWrap>
                  <EmbroideryModalPreviewCarousel />
                </Styles.ImagePreviewWrap>
              </EmbroideryPreviewContainer>
            </Styles.Section>
            <Styles.Section>
              <EmbroideryOptionSelector
                isB2BColorSelected={isB2BColorSelected}
                handleBackButtonClick={handleBackButtonClick}
              />
            </Styles.Section>
          </Styles.Container>
        </Styles.DialogBody>
      </Styles.Dialog>
    </NextDialogProvider>
  )
}

const EmbroideryPreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`
